import { Combobox } from '@/global/components/Combobox'
import { useDebouncedValue } from '@/hooks'
import { cn } from '@/lib/utils'
import { onlyDefined, uniqueBy } from '@acme/common'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { type Cliente, clientes } from 'service/api'

const DEBOUNCE_BUSCA_MS = 300

type SelectClientesProps = {
    label?: string
    /** Texto do gatilho quando nada está selecionado. */
    placeholder?: string
    disabled?: boolean
}

type Selecao =
    | { multiple: true; value: number[]; onChange: (ids: number[]) => void }
    | {
          multiple?: false
          value: number | null
          onChange: (id: number | null) => void
      }

type SelectClientesPropsForm = SelectClientesProps & {
    name: string
    multiple?: boolean
    value?: never
    onChange?: never
}

type SelectClientesPropsWithoutForm = SelectClientesProps & {
    name?: never
} & Selecao

/** Na seleção única, escolher de novo o cliente atual volta para `null`. */
function desmarcarAoRepetir(selecao: Selecao): Selecao {
    if (selecao.multiple) return selecao
    const { value, onChange } = selecao
    return { value, onChange: id => onChange(id === value ? null : id) }
}

/**
 * Campo de clientes, de um ou vários (`multiple`). Com `name` grava no
 * react-hook-form; sem `name` é controlado por `value`/`onChange`.
 *
 * Busca e paginação ficam no servidor porque a API limita `perPage` a 100:
 * pedir a lista inteira de uma vez esconderia os clientes seguintes.
 */
export function SelectClientes(
    props: SelectClientesPropsForm | SelectClientesPropsWithoutForm,
) {
    const { name, label = 'Clientes', placeholder, disabled } = props
    // Fora de um FormProvider o contexto é nulo.
    const control = useFormContext()?.control
    const [busca, setBusca] = useState('')
    const buscaDebounced = useDebouncedValue(busca.trim(), DEBOUNCE_BUSCA_MS)

    const {
        data: paginas,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
    } = clientes.useInfiniteScroll<Cliente>({
        queryParams: (buscaDebounced
            ? { search: buscaDebounced }
            : {}) as never,
    })
    const [cached, setCached] = useState<Cliente[]>([])

    const carregados = useMemo(
        () => paginas?.pages.flatMap(pagina => pagina.data) ?? [],
        [paginas],
    )
    useEffect(() => {
        setCached(prev =>
            uniqueBy(
                prev.concat(
                    (paginas?.pages?.flatMap(it => it.data) ?? []).filter(
                        onlyDefined,
                    ),
                ),
                'id',
            ),
        )
    }, [paginas])

    const opcoes = useMemo(
        () => carregados.map(cliente => cliente.id),
        [carregados],
    )

    const nomePorId = useMemo(
        () =>
            new Map(cached.map(cliente => [cliente.id, cliente.nomeFantasia])),
        [cached],
    )

    // O cliente selecionado pode estar fora das páginas já carregadas — nesse
    // caso só o id é conhecido.
    const rotuloDe = useCallback(
        (id: number) => nomePorId.get(id) ?? `#${id}`,
        [nomePorId],
    )

    const carregarMais = useCallback(() => {
        if (!isFetchingNextPage) fetchNextPage()
    }, [fetchNextPage, isFetchingNextPage])

    const renderCombobox = (
        selecao: Selecao,
        isDisabled: boolean | undefined,
        erro?: string,
    ) => (
        <Combobox<number>
            {...desmarcarAoRepetir(selecao)}
            id={`${name ?? 'clientes'}-select`}
            label={label}
            placeholder={placeholder}
            options={opcoes}
            getOptionLabel={rotuloDe}
            getOptionKey={id => String(id)}
            isOptionEqualToValue={(opcao, id) => opcao === id}
            inputValue={busca}
            onInputChange={setBusca}
            loading={isFetching && !isFetchingNextPage}
            hasMore={hasNextPage}
            onLoadMore={carregarMais}
            loadingMore={isFetchingNextPage}
            disabled={isDisabled}
            noOptionsText="Nenhum cliente encontrado"
            unHelperText={!control}
            error={!!erro}
            helperText={erro}
            className={cn({ 'w-[260px]': !control })}
            size={!control ? 'small' : 'medium'}
        />
    )

    if (props.name === undefined) {
        const selecao: Selecao = props.multiple
            ? { multiple: true, value: props.value, onChange: props.onChange }
            : { value: props.value, onChange: props.onChange }
        return renderCombobox(selecao, disabled)
    }

    const { multiple } = props
    return (
        <Controller
            name={props.name}
            control={control}
            defaultValue={multiple ? [] : null}
            render={({ field, fieldState: { error } }) =>
                renderCombobox(
                    multiple
                        ? {
                              multiple: true,
                              value: field.value ?? [],
                              onChange: field.onChange,
                          }
                        : {
                              value: field.value ?? null,
                              onChange: field.onChange,
                          },
                    disabled || (control as any)?._options?.disabled,
                    error?.message,
                )
            }
        />
    )
}
